// Local persistence. IndexedDB via idb-keyval, no backend.
//
// Documents, findings and reviews live in the user's browser and nowhere else.
// Clearing site data deletes them; nothing is recoverable from a server,
// because there is no server.
//
// The store is created on first use rather than at import, and every operation
// tolerates its absence. A browser with storage blocked — private mode, an
// enterprise policy, a federal desktop with tightened settings — must still be
// able to read a review. Persistence is a convenience here, never a
// precondition.
//
// Everything below goes through a small Backend interface rather than calling
// idb-keyval directly. That is not indirection for its own sake: the schema
// migration is the one piece of this file whose correctness matters most and
// which is hardest to observe, and the interface is what lets it be tested
// against an in-memory store under plain node, where there is no IndexedDB.
// See docs/redesign/ARCHITECTURE.md.

import { createStore, del, get, keys, set, type UseStore } from 'idb-keyval';

import { enrichFlag } from '../engine/finding';
import type {
  ExtractedPages,
  FindingsRecord,
  Flag,
  FlagDraft,
  MutePrompts,
  MutedCheck,
  MutedChecks,
  ReviewRecord,
  SourceDocument,
} from '../types';
import { addDismissals, newReviewRecord, normaliseReviewRecord } from './review';
import type { DismissalTally } from './review';

/* ==========================================================================
   Backend
   ========================================================================== */

export interface Backend {
  get<T>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
  del(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

function idbBackend(s: UseStore): Backend {
  return {
    get: <T,>(key: string) => get<T>(key, s),
    set: (key, value) => set(key, value, s),
    del: (key) => del(key, s),
    keys: async () =>
      (await keys(s)).filter((k): k is string => typeof k === 'string'),
  };
}

/**
 * An in-memory Backend. Used by the check harness, and available to any caller
 * that wants the store's logic without its storage.
 */
export function createMemoryBackend(seed?: Record<string, unknown>): Backend {
  const map = new Map<string, unknown>(Object.entries(seed ?? {}));
  return {
    get: async <T,>(key: string) => map.get(key) as T | undefined,
    set: async (key, value) => void map.set(key, value),
    del: async (key) => void map.delete(key),
    keys: async () => [...map.keys()],
  };
}

let cached: Backend | null | undefined;

function backendOrNull(): Backend | null {
  if (cached !== undefined) return cached;
  try {
    cached =
      typeof indexedDB === 'undefined'
        ? null
        : idbBackend(createStore('ceiling', 'records'));
  } catch {
    cached = null;
  }
  return cached;
}

/** True when review marks and documents will survive a reload. */
export function storageAvailable(): boolean {
  return backendOrNull() !== null;
}

/**
 * IndexedDB can fail to answer rather than fail outright: an open() blocked by
 * another tab's version-change transaction never settles, and a page that
 * awaited it would sit on its loading state forever with no way out. Every read
 * is therefore bounded, and a read that does not come back is treated as "no
 * stored data", which every caller already handles.
 */
const READ_TIMEOUT_MS = 4000;

async function withTimeout<T>(
  operation: string,
  work: Promise<T>,
  fallback: T,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((resolve) => {
    timer = setTimeout(() => {
      console.warn(
        `[store] ${operation} did not respond within ${READ_TIMEOUT_MS}ms; continuing without stored data`,
      );
      resolve(fallback);
    }, READ_TIMEOUT_MS);
  });
  try {
    return await Promise.race([work, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

const DOC_PREFIX = 'doc:';
const ANALYSIS_PREFIX = 'analysis:';
const FLAG_STATE_PREFIX = 'flags:';
const FINDINGS_PREFIX = 'findings:';
const PAGES_PREFIX = 'pages:';
const REVIEW_PREFIX = 'review:';
/**
 * Records an earlier version wrote after a clause-pattern run. Nothing
 * writes them now — that stage is the one thing that would send a document
 * out of the browser, and no review runs it — but a browser upgraded from
 * that version can still be holding one, and deleting a review has to take
 * it too. The prefix exists for that one deletion.
 */
const MODEL_CACHE_PREFIX = 'model:';
const LAST_DOCUMENT_KEY = 'last-document-id';
const MUTED_CHECKS_KEY = 'muted-checks';
const MUTE_PROMPTS_KEY = 'mute-prompts';
const UI_PREFS_KEY = 'ui-prefs';
const SCHEMA_VERSION_KEY = 'schema-version';

/* ==========================================================================
   Schema version and migration
   --------------------------------------------------------------------------
   idb-keyval opens its database at version 1 and offers no upgrade hook, so
   the schema version is a record inside the store rather than an IndexedDB
   version. That is a real constraint of the dependency, not a preference: a
   version bump would mean dropping idb-keyval, and BRIEF.md admits no new
   dependency for it.

   Every step is idempotent and skips a target that already exists, so a
   migration interrupted half way — a closed tab, a read that timed out —
   resumes correctly rather than writing twice or losing a record.
   ========================================================================== */

export const SCHEMA_VERSION = 3;

const migrations = new WeakMap<Backend, Promise<void>>();

function ensureMigrated(backend: Backend): Promise<void> {
  const running = migrations.get(backend);
  if (running) return running;
  const started = withTimeout('migrate', migrate(backend), undefined).then(
    () => undefined,
  );
  migrations.set(backend, started);
  return started;
}

/**
 * Upgrades whatever is in this browser to the current schema.
 *
 * Version 1 was the pre-review schema: `doc:`, `analysis:`, `flags:`, `model:`
 * and `last-document-id`, with no version record of its own. Version 2 adds the
 * review, the findings record and the extracted pages the document pane reads.
 * Version 3 removes the `flags:` marks: version 2 copied them into a review's
 * dispositions and left them where the shipped report page could still read
 * them, and step 9 retired that page.
 */
export async function migrate(backend: Backend): Promise<number> {
  const stored = await backend.get<number>(SCHEMA_VERSION_KEY);

  if (stored === undefined) {
    const existing = await backend.keys();
    if (existing.length === 0) {
      // Nothing has ever been stored here. There is nothing to upgrade.
      await backend.set(SCHEMA_VERSION_KEY, SCHEMA_VERSION);
      return SCHEMA_VERSION;
    }
    await migrateV1ToV2(backend);
    await migrateV2ToV3(backend);
    await backend.set(SCHEMA_VERSION_KEY, SCHEMA_VERSION);
    return SCHEMA_VERSION;
  }

  if (stored < SCHEMA_VERSION) {
    if (stored < 2) await migrateV1ToV2(backend);
    if (stored < 3) await migrateV2ToV3(backend);
    await backend.set(SCHEMA_VERSION_KEY, SCHEMA_VERSION);
  }
  return SCHEMA_VERSION;
}

/**
 * The v1 flag mark. Nothing writes this shape any more; the migration reads it
 * to convert marks into dispositions, and then version 3 deletes the records.
 */
interface LegacyFlagState {
  status: 'open' | 'confirmed' | 'dismissed';
  note?: string;
  updatedAt: string;
}

type LegacyFlagStates = Record<string, LegacyFlagState>;

/** The v1 analysis record. Kept so the migration can describe what it reads. */
interface LegacyAnalysisRecord {
  id: string;
  documentId: string;
  createdAt: string;
  flags: unknown[];
}

async function migrateV1ToV2(backend: Backend): Promise<void> {
  const allKeys = await backend.keys();

  // --- analysis: → findings: -------------------------------------------
  // Findings stored before this schema predate planArea, claim, contextLine,
  // commentDraft and quoteOffsets, so they are put through the same enrichment
  // a freshly run check goes through. A finding whose failure mode is no longer
  // in the taxonomy cannot be enriched; it is carried over unchanged rather
  // than dropped, because losing a reviewer's findings is worse than a finding
  // that renders thinly.
  for (const key of allKeys.filter((k) => k.startsWith(ANALYSIS_PREFIX))) {
    const docId = key.slice(ANALYSIS_PREFIX.length);
    const legacy = await backend.get<LegacyAnalysisRecord>(key);
    if (!legacy) {
      await backend.del(key);
      continue;
    }
    const target = `${FINDINGS_PREFIX}${docId}`;
    if ((await backend.get(target)) === undefined) {
      const flags = (legacy.flags ?? []).map((raw) => {
        try {
          return enrichFlag(raw as FlagDraft);
        } catch {
          return raw as Flag;
        }
      });
      await backend.set(target, {
        docId,
        createdAt: legacy.createdAt ?? '',
        flags,
        ranChecks: [...new Set(flags.map((f) => f.checkId).filter(Boolean))],
      } satisfies FindingsRecord);
    }
    // The data now lives under findings:, so the old key is redundant rather
    // than lost.
    await backend.del(key);
  }

  // --- doc: → pages: ----------------------------------------------------
  for (const key of allKeys.filter((k) => k.startsWith(DOC_PREFIX))) {
    const docId = key.slice(DOC_PREFIX.length);
    const target = `${PAGES_PREFIX}${docId}`;
    if ((await backend.get(target)) !== undefined) continue;
    const doc = await backend.get<SourceDocument>(key);
    if (!doc) continue;
    await backend.set(target, pagesOf(doc));
  }

  // --- flags: → review: -------------------------------------------------
  // Confirmations and dismissals made under the old flag sheet become
  // dispositions on a review. A dismissal carried no reason in v1 and none is
  // invented here: the field stays empty, which is what actually happened.
  //
  // The keys are left where they are by this step and removed by the next one.
  // The order matters: a browser interrupted between the two must not be
  // re-migrated into an empty review, so the source survives until the review
  // that replaces it has been written.
  for (const key of allKeys.filter((k) => k.startsWith(FLAG_STATE_PREFIX))) {
    const docId = key.slice(FLAG_STATE_PREFIX.length);
    const target = `${REVIEW_PREFIX}${docId}`;
    if ((await backend.get(target)) !== undefined) continue;
    const states = await backend.get<LegacyFlagStates>(key);
    if (!states || Object.keys(states).length === 0) continue;

    const doc = await backend.get<SourceDocument>(`${DOC_PREFIX}${docId}`);
    const review = newReviewRecord(docId, doc?.fileName ?? '', '');
    for (const [findingId, state] of Object.entries(states)) {
      if (state.status === 'open') continue;
      review.dispositions[findingId] = {
        findingId,
        decision: state.status === 'confirmed' ? 'include' : 'not-a-concern',
        note: state.note,
        decidedAt: state.updatedAt ?? '',
      };
    }
    review.createdAt = review.updatedAt = newestUpdate(states);
    await backend.set(target, review);
  }
}

/**
 * Version 3: the `flags:` marks go.
 *
 * Version 2 copied them into each review's dispositions and left them in place
 * because the report page on screen at the time still read and wrote them. Step
 * 9 replaced that page with the workspace, which reads dispositions only, so
 * the marks are now a second copy of a decision that nothing consults — exactly
 * the drift the review record exists to prevent. Deleting a key twice is a
 * no-op, so this is idempotent by construction.
 */
async function migrateV2ToV3(backend: Backend): Promise<void> {
  for (const key of (await backend.keys()).filter((k) =>
    k.startsWith(FLAG_STATE_PREFIX),
  )) {
    await backend.del(key);
  }
}

function newestUpdate(states: LegacyFlagStates): string {
  return Object.values(states)
    .map((s) => s.updatedAt ?? '')
    .sort()
    .reverse()[0] ?? '';
}

/**
 * Resolves the backend and guarantees the migration has run before anything
 * reads or writes. `explicit` is how the check harness supplies an in-memory
 * backend; the app never passes one.
 */
async function ready(explicit?: Backend): Promise<Backend | null> {
  const backend = explicit ?? backendOrNull();
  if (!backend) return null;
  await ensureMigrated(backend);
  return backend;
}

/* ==========================================================================
   Documents
   ========================================================================== */

/**
 * Enough to list ingested documents without loading every page of text.
 */
export interface DocumentSummary {
  id: string;
  fileName: string;
  pageCount: number;
  ingestedAt: string;
  sectionCount: number;
  tableCount: number;
  citationCount: number;
}

export function summarise(doc: SourceDocument): DocumentSummary {
  return {
    id: doc.id,
    fileName: doc.fileName,
    pageCount: doc.pageCount,
    ingestedAt: doc.ingestedAt,
    sectionCount: doc.sections.length,
    tableCount: doc.tables.length,
    citationCount: doc.citations.length,
  };
}

function pagesOf(doc: SourceDocument): ExtractedPages {
  return {
    docId: doc.id,
    pageCount: doc.pageCount,
    pages: doc.pages.map((p) => ({ number: p.number, text: p.text })),
    extractedAt: doc.ingestedAt,
  };
}

/**
 * The page text is written twice: once inside the SourceDocument, which the
 * engine needs whole, and once as its own record. The duplicate is deliberate.
 * The document pane opens on a single page and must not have to read a
 * document's rawText, sections, tables and citations back out of storage to do
 * it. deleteReview() removes both.
 */
export async function putDocument(
  doc: SourceDocument,
  backend?: Backend,
): Promise<void> {
  const b = await ready(backend);
  if (!b) return;
  await b.set(`${DOC_PREFIX}${doc.id}`, doc);
  await b.set(`${PAGES_PREFIX}${doc.id}`, pagesOf(doc));
}

export async function getDocument(
  id: string,
  backend?: Backend,
): Promise<SourceDocument | undefined> {
  const b = await ready(backend);
  if (!b) return undefined;
  return withTimeout('getDocument', b.get<SourceDocument>(`${DOC_PREFIX}${id}`), undefined);
}

/** The page text alone, for the document pane. */
export async function getPages(
  docId: string,
  backend?: Backend,
): Promise<ExtractedPages | undefined> {
  const b = await ready(backend);
  if (!b) return undefined;
  return withTimeout('getPages', b.get<ExtractedPages>(`${PAGES_PREFIX}${docId}`), undefined);
}

export async function listDocuments(backend?: Backend): Promise<DocumentSummary[]> {
  const b = await ready(backend);
  if (!b) return [];
  return withTimeout('listDocuments', allDocuments(b), []);
}

async function allDocuments(b: Backend): Promise<DocumentSummary[]> {
  const docKeys = (await b.keys()).filter((k) => k.startsWith(DOC_PREFIX));
  const docs = await Promise.all(docKeys.map((k) => b.get<SourceDocument>(k)));

  return docs
    .filter((d): d is SourceDocument => Boolean(d))
    .map(summarise)
    .sort((a, b2) => b2.ingestedAt.localeCompare(a.ingestedAt));
}

/* ==========================================================================
   Findings
   ========================================================================== */

export async function putFindings(
  record: FindingsRecord,
  backend?: Backend,
): Promise<void> {
  const b = await ready(backend);
  if (!b) return;
  await b.set(`${FINDINGS_PREFIX}${record.docId}`, record);
}

export async function getFindings(
  docId: string,
  backend?: Backend,
): Promise<FindingsRecord | undefined> {
  const b = await ready(backend);
  if (!b) return undefined;
  return withTimeout(
    'getFindings',
    b.get<FindingsRecord>(`${FINDINGS_PREFIX}${docId}`),
    undefined,
  );
}

/** Remembers which document the ingestion page was last working with. */
export async function setLastDocumentId(id: string, backend?: Backend): Promise<void> {
  const b = await ready(backend);
  if (!b) return;
  await b.set(LAST_DOCUMENT_KEY, id);
}

export async function getLastDocumentId(
  backend?: Backend,
): Promise<string | undefined> {
  const b = await ready(backend);
  if (!b) return undefined;
  return withTimeout('getLastDocumentId', b.get<string>(LAST_DOCUMENT_KEY), undefined);
}

/* ==========================================================================
   The review
   --------------------------------------------------------------------------
   What the reviewer decided, and the comments they will send. This is the
   product's output, so it is written on every decision rather than at the end.
   ========================================================================== */

/**
 * Reads through the same per-review queue the writes use.
 *
 * A read is not a write, but it has to be ordered against them: a reviewer who
 * decides a finding and immediately opens the comment sheet issues the read
 * while the write is still in flight. Answering it from before the write
 * would tell them the decision they just made is not there.
 */
export async function getReview(
  docId: string,
  backend?: Backend,
): Promise<ReviewRecord | undefined> {
  const key = `${REVIEW_PREFIX}${docId}`;
  return enqueue(key, async () => {
    const b = await ready(backend);
    if (!b) {
      // No storage. The session fallback is the only record there is.
      const held = memoryReviews.get(key);
      return held ? normaliseReviewRecord(held, docId) : undefined;
    }
    const stored = await withTimeout('getReview', b.get<ReviewRecord>(key), undefined);
    return stored ? normaliseReviewRecord(stored, docId) : undefined;
  });
}

export async function putReview(
  record: ReviewRecord,
  backend?: Backend,
): Promise<void> {
  const b = await ready(backend);
  if (!b) return;
  await b.set(`${REVIEW_PREFIX}${record.docId}`, record);
}

/**
 * Read, change, write — serialised per review, for the same reason flag marks
 * were: two decisions made in quick succession would otherwise both read the
 * same "before" record and the second would clobber the first.
 */
export async function updateReview(
  docId: string,
  fileName: string,
  change: (record: ReviewRecord) => ReviewRecord,
  backend?: Backend,
): Promise<ReviewRecord> {
  const key = `${REVIEW_PREFIX}${docId}`;
  return enqueue(key, async () => {
    const b = await ready(backend);
    const current =
      (b ? await b.get<ReviewRecord>(key) : memoryReviews.get(key)) ??
      newReviewRecord(docId, fileName);
    const next: ReviewRecord = {
      ...change(normaliseReviewRecord(current, docId)),
      updatedAt: new Date().toISOString(),
    };
    if (b) await b.set(key, next);
    else memoryReviews.set(key, next);
    return next;
  });
}

export async function listReviews(backend?: Backend): Promise<ReviewRecord[]> {
  const b = await ready(backend);
  if (!b) return [];
  return withTimeout('listReviews', allReviews(b), []);
}

async function allReviews(b: Backend): Promise<ReviewRecord[]> {
  const reviewKeys = (await b.keys()).filter((k) => k.startsWith(REVIEW_PREFIX));
  const records = await Promise.all(reviewKeys.map((k) => b.get<ReviewRecord>(k)));
  return records
    .filter((r): r is ReviewRecord => Boolean(r))
    .map((r) => normaliseReviewRecord(r, r.docId))
    .sort((a, b2) => b2.updatedAt.localeCompare(a.updatedAt));
}

/**
 * Removes every trace of one review: the parsed document, the page text the
 * document pane reads, the findings, the reviewer's decisions and comments, the
 * cached classification, and the legacy marks.
 *
 * This is the control a federal user needs in order to leave nothing behind, so
 * "every trace" is meant literally and tools/feedback-check.tsx enumerates the
 * store afterwards to prove it. What survives is only what was never about this
 * document: the muted checks and the offers the reviewer declined, which are
 * facts about how this reviewer works, are per browser rather than per review,
 * and are cleared from /checks.
 */
export async function deleteReview(docId: string, backend?: Backend): Promise<void> {
  const b = await ready(backend);
  memoryReviews.delete(`${REVIEW_PREFIX}${docId}`);
  if (!b) return;

  for (const key of [
    `${DOC_PREFIX}${docId}`,
    `${PAGES_PREFIX}${docId}`,
    `${FINDINGS_PREFIX}${docId}`,
    `${ANALYSIS_PREFIX}${docId}`,
    `${REVIEW_PREFIX}${docId}`,
    `${FLAG_STATE_PREFIX}${docId}`,
    `${MODEL_CACHE_PREFIX}${docId}`,
  ]) {
    await b.del(key);
  }

  // A pointer to a document that no longer exists would send the next visit to
  // a route that cannot load.
  if ((await b.get<string>(LAST_DOCUMENT_KEY)) === docId) {
    await b.del(LAST_DOCUMENT_KEY);
  }
}

/**
 * How often each check has been set aside, and for what reason, across every
 * review in this browser.
 *
 * This is the third feedback loop's input, and it is derived rather than kept:
 * a running count would drift the first time a reviewer changed their mind, and
 * would survive a review they deleted. Recomputing costs one pass over the
 * reviews and their findings, once, when the workspace opens.
 *
 * `exclude` leaves out the review currently on screen. The workspace holds that
 * one in memory and adds it live, so that a third dismissal made in this
 * session raises the offer without waiting for a reload.
 */
export async function getDismissalTally(
  exclude?: string,
  backend?: Backend,
): Promise<DismissalTally> {
  const b = await ready(backend);
  if (!b) return {};
  return withTimeout('getDismissalTally', dismissalTally(b, exclude), {});
}

async function dismissalTally(
  b: Backend,
  exclude?: string,
): Promise<DismissalTally> {
  const reviews = (await allReviews(b)).filter((r) => r.docId !== exclude);
  let tally: DismissalTally = {};

  for (const review of reviews) {
    if (Object.keys(review.dispositions).length === 0) continue;
    const findings = await b.get<FindingsRecord>(
      `${FINDINGS_PREFIX}${review.docId}`,
    );
    // A review whose findings are no longer here — deleted, or written by a
    // build that stored them differently — contributes nothing rather than
    // being guessed at.
    if (!findings) continue;
    const modes = new Map(findings.flags.map((f) => [f.id, f.failureModeId]));
    tally = addDismissals(tally, review.dispositions, (id) => modes.get(id));
  }

  return tally;
}

/* ==========================================================================
   Muted checks
   --------------------------------------------------------------------------
   Per browser, not per review: that a reviewer keeps setting the same check
   aside for the same reason is a fact about their work, not about one plan.
   ========================================================================== */

export async function getMutedChecks(backend?: Backend): Promise<MutedChecks> {
  const b = await ready(backend);
  if (!b) return {};
  return (
    (await withTimeout('getMutedChecks', b.get<MutedChecks>(MUTED_CHECKS_KEY), undefined)) ??
    {}
  );
}

export async function setMutedCheck(
  entry: MutedCheck,
  backend?: Backend,
): Promise<MutedChecks> {
  return enqueue(MUTED_CHECKS_KEY, async () => {
    const b = await ready(backend);
    if (!b) return {};
    const current = (await b.get<MutedChecks>(MUTED_CHECKS_KEY)) ?? {};
    const next: MutedChecks = { ...current, [entry.failureModeId]: entry };
    await b.set(MUTED_CHECKS_KEY, next);
    return next;
  });
}

export async function unmuteCheck(
  failureModeId: MutedCheck['failureModeId'],
  backend?: Backend,
): Promise<MutedChecks> {
  return enqueue(MUTED_CHECKS_KEY, async () => {
    const b = await ready(backend);
    if (!b) return {};
    const current = (await b.get<MutedChecks>(MUTED_CHECKS_KEY)) ?? {};
    const next = { ...current };
    delete next[failureModeId];
    await b.set(MUTED_CHECKS_KEY, next);
    return next;
  });
}

/* --------------------------------------------------------------------------
   "Keep showing it"
   --------------------------------------------------------------------------
   The other answer to the same offer. Recorded for the same reason the
   dismissal reason is: a reviewer who has said they want to keep seeing a
   check should not be asked again about the same three decisions. Keyed by
   check and reason together — `mutePromptKey()` in src/data/review.ts.
   -------------------------------------------------------------------------- */

export async function getMutePrompts(backend?: Backend): Promise<MutePrompts> {
  const b = await ready(backend);
  if (!b) return {};
  return (
    (await withTimeout(
      'getMutePrompts',
      b.get<MutePrompts>(MUTE_PROMPTS_KEY),
      undefined,
    )) ?? {}
  );
}

/** Records that the offer was declined at this many dismissals. */
export async function declineMutePrompt(
  key: string,
  count: number,
  backend?: Backend,
): Promise<MutePrompts> {
  return enqueue(MUTE_PROMPTS_KEY, async () => {
    const b = await ready(backend);
    const declined: MutePrompts = { [key]: count };
    if (!b) return declined;
    const current = (await b.get<MutePrompts>(MUTE_PROMPTS_KEY)) ?? {};
    const next: MutePrompts = { ...current, [key]: count };
    await b.set(MUTE_PROMPTS_KEY, next);
    return next;
  });
}

/* ==========================================================================
   Interface preferences
   --------------------------------------------------------------------------
   Per browser, not per review. Whether the document pane is open is a fact
   about how this reviewer likes to work, in the same way a muted check is; it
   should not have to be re-collapsed on every document they open.
   ========================================================================== */

export interface UiPrefs {
  /** Whether the workspace's document pane is expanded. */
  documentPaneOpen: boolean;
}

function defaultUiPrefs(): UiPrefs {
  return { documentPaneOpen: true };
}

export async function getUiPrefs(backend?: Backend): Promise<UiPrefs> {
  const b = await ready(backend);
  if (!b) return defaultUiPrefs();
  const stored = await withTimeout(
    'getUiPrefs',
    b.get<Partial<UiPrefs>>(UI_PREFS_KEY),
    undefined,
  );
  return { ...defaultUiPrefs(), ...(stored ?? {}) };
}

export async function setUiPrefs(
  patch: Partial<UiPrefs>,
  backend?: Backend,
): Promise<UiPrefs> {
  return enqueue(UI_PREFS_KEY, async () => {
    const b = await ready(backend);
    if (!b) return { ...defaultUiPrefs(), ...patch };
    const current =
      (await b.get<Partial<UiPrefs>>(UI_PREFS_KEY)) ?? defaultUiPrefs();
    const next: UiPrefs = { ...defaultUiPrefs(), ...current, ...patch };
    await b.set(UI_PREFS_KEY, next);
    return next;
  });
}

/* ==========================================================================
   The write queue
   --------------------------------------------------------------------------
   Every write above is a read-modify-write, so two decisions made in quick
   succession would otherwise both read the same "before" record and the second
   would clobber the first. Queueing per key costs nothing and makes a lost
   decision impossible.
   ========================================================================== */

const writeQueues = new Map<string, Promise<unknown>>();

function enqueue<T>(key: string, task: () => Promise<T>): Promise<T> {
  const previous = writeQueues.get(key) ?? Promise.resolve();
  const next = previous.then(task, task);
  // Keep the chain going even if a link rejects, and let it be collected once
  // it is the newest entry.
  const settled = next.catch(() => undefined);
  writeQueues.set(key, settled);
  void settled.finally(() => {
    if (writeQueues.get(key) === settled) writeQueues.delete(key);
  });
  return next;
}

/**
 * Session fallback for browsers where IndexedDB is unavailable. A decision then
 * lasts for the session and is honestly lost on reload, rather than
 * disappearing while the reviewer is still working.
 */
const memoryReviews = new Map<string, ReviewRecord>();
